import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, Mic, Square } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StatusPill } from "../components/StatusPill";
import { BigActionButton } from "../components/BigActionButton";
import { TranscriptPanel } from "../components/TranscriptPanel";
import { DebugPanel } from "../components/DebugPanel";
import { useSpeaker } from "../hooks/useSpeaker";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "az", label: "Azərbaycanca", flag: "🇦🇿" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
];

export default function Speaker() {
  const navigate = useNavigate();
  const s = useSpeaker();
  const targetLang = LANGUAGES.find((l) => l.code === s.target) || LANGUAGES[0];
  const isLive = s.phase === "live";

  const statusState = isLive ? "live" : s.phase === "connecting" ? "connecting" : s.phase === "error" ? "error" : "wait";
  const statusLabel = isLive ? "CANLI" : s.phase === "connecting" ? "Bağlanıyor…" : s.phase === "error" ? "Hata" : "Hazır";

  const ctaState = s.phase === "connecting" ? "connecting" : isLive ? "live" : "idle";
  const ctaLabel = s.phase === "connecting" ? "BAĞLANIYOR…" : isLive ? "CANLI YAYINI DURDUR" : s.phase === "error" ? "TEKRAR DENE" : "CANLI YAYINI BAŞLAT";

  const openaiLabel = { connected: "Bağlı", connecting: "Bağlanıyor…", disconnected: "Kapalı", error: "Hata" }[s.openaiStatus] || s.openaiStatus;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto flex min-h-screen max-w-[520px] flex-col gap-5 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button
            data-testid="speaker-back-button"
            onClick={() => { s.stop(); navigate("/"); }}
            aria-label="Geri"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/40 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Badge variant="secondary" className="font-mono text-xs" style={{ fontFamily: "'Roboto Mono', monospace" }}>DEMO01</Badge>
          <StatusPill state={statusState} label={statusLabel} testId="global-connection-status-pill" />
        </div>

        {/* Target language quick-switch */}
        <div className="rounded-[var(--radius)] border border-border/80 bg-card/80 p-4 backdrop-blur">
          <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground" data-testid="speaker-direction-label">
            <Globe className="h-4 w-4 text-[hsl(var(--accent))]" />
            Otomatik algıla
            <span className="text-muted-foreground">→</span>
            <span className="font-semibold text-[hsl(var(--accent))]">{targetLang.label}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                data-testid={`speaker-target-${lang.code}`}
                onClick={() => s.setTarget(lang.code)}
                className={`flex h-12 items-center justify-center gap-2 rounded-[calc(var(--radius)-6px)] border text-sm font-medium transition-colors active:scale-[0.98] ${
                  s.target === lang.code
                    ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]"
                    : "border-border/70 bg-secondary/40 text-muted-foreground hover:text-foreground"
                }`}
              >
                <span>{lang.flag}</span>
                {lang.label}
              </button>
            ))}
          </div>
        </div>

        {/* Live banner */}
        {isLive && (
          <div data-testid="speaker-live-banner" className="flex items-center justify-center gap-2 rounded-[calc(var(--radius)-4px)] border border-[hsl(var(--state-live))]/30 bg-[hsl(var(--state-live))]/10 px-3 py-2 text-sm font-medium text-[hsl(var(--state-live))]">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[hsl(var(--state-live))]" />
            CANLI · 🌐 → {targetLang.label}
            {/* mic level */}
            <span className="ml-2 inline-flex h-3 w-16 overflow-hidden rounded-full bg-secondary">
              <span className="h-full rounded-full bg-[hsl(var(--state-live))] transition-[width] duration-150" style={{ width: `${Math.round(s.micLevel * 100)}%` }} />
            </span>
          </div>
        )}

        {/* Primary CTA */}
        <BigActionButton
          state={ctaState}
          onClick={isLive ? s.stop : s.start}
          icon={isLive ? Square : Mic}
          testId="speaker-live-toggle-button"
        >
          {ctaLabel}
        </BigActionButton>

        {s.error && (
          <div data-testid="speaker-error-banner" className="rounded-[calc(var(--radius)-4px)] border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-[hsl(var(--state-error))]">
            {s.error}
          </div>
        )}

        {/* Transcripts */}
        <Tabs defaultValue="original" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="original" data-testid="speaker-original-tab">ORIGINAL</TabsTrigger>
            <TabsTrigger value="translation" data-testid="speaker-translation-tab">TRANSLATION</TabsTrigger>
          </TabsList>
          <TabsContent value="original">
            <TranscriptPanel
              lines={s.originalLines}
              partial={s.originalPartial}
              emptyText="Henüz metin yok. Konuşmaya başlayın."
              testId="speaker-transcript-panel"
            />
          </TabsContent>
          <TabsContent value="translation">
            <TranscriptPanel
              lines={s.translationLines}
              partial={s.translationPartial}
              emptyText="Çeviri burada görünecek."
              testId="speaker-translation-panel"
            />
          </TabsContent>
        </Tabs>

        {/* Debug */}
        <div className="mt-auto">
          <DebugPanel
            testId="speaker-debug-open-button"
            rows={[
              { label: "Mikrofon", value: s.micOn ? "Açık" : "Kapalı", valueClass: s.micOn ? "text-[hsl(var(--state-live))]" : "text-muted-foreground" },
              { label: "OpenAI", value: openaiLabel, valueClass: s.openaiStatus === "connected" ? "text-[hsl(var(--state-live))]" : s.openaiStatus === "error" ? "text-[hsl(var(--state-error))]" : "text-muted-foreground" },
              { label: "Broadcast WS", value: s.wsStatus === "connected" ? "Bağlı" : "Kapalı", valueClass: s.wsStatus === "connected" ? "text-[hsl(var(--state-live))]" : "text-muted-foreground" },
              { label: "Dinleyici sayısı", value: String(s.listeners) },
              { label: "Yaklaşık gecikme", value: s.latencyMs != null ? `${s.latencyMs} ms` : "—" },
            ]}
          />
        </div>
      </main>
    </div>
  );
}
